import { Formik } from "formik";
import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetCameraConfigQuery,
  useUpdateConfigurationSingleDeviceMutation,
} from "../../../../api/network/vmApiService";
import { setSingleVMCameraCurrentConfigs } from "../../../../api/store/commonSlice";
import { CameraDeviceConfigProfile } from "../../../../api/types/Camera";
import svgIcons from "../../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../../constants/constants";
import { routeUrls } from "../../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../../utils/user";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../../../admin-details/account/constants";
import HeaderView from "../../components/HeaderView";
import SubHeaderView from "../../components/SubHeaderView";
import { systemSettingFormInitialValues } from "../../validation";
import CameraSystemSettings from "./camera-system-settings";
import VideoRecordingSettings from "./video-recording-setting";

const ScreenCameraSettings: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.camera_settings",
  });

  const navigationTypeRef = useRef<"none" | "next" | "previous">("none");
  const { cameraId } = useParams<{ cameraId: string }>();
  const { state: deviceData } = useLocation();
  console.log(deviceData);
  const model = deviceData?.model || "";
  const isSingleConfiguration = deviceData?.isSingleConfiguration || false;
  const isMassSingleConfiguration = deviceData?.isMassSingleConfiguration || false;
  const cameraName = deviceData?.cameraName || null;
  const cameraIds = deviceData?.cameraIds || [];
  const baseModel = model.replace(/C$/, "");
  const [updateConfigurationSingleApiDeviceTrigger] =
    useUpdateConfigurationSingleDeviceMutation();
  const [selectedSettings, setSelectedSettings] = useState<string | null>(
    "system"
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allCameraConfigsInStore = useSelector(
    (state: {
      commonReducer: {
        singleVMCameraCurrentConfigs: {
          [key: string]: CameraDeviceConfigProfile;
        };
      };
    }) => state.commonReducer.singleVMCameraCurrentConfigs
  );
  const thisCameraConfigInStore =
    allCameraConfigsInStore?.[cameraId as string] || {};
  const SideMenu = [
    { name: "system", label: t("system") },
    { name: "video_recording", label: t("video_recording") },
  ];

  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  const handleLeftNavigation = () => { };

  const handleRightNavigation = () => {
    navigate(
      isSingleConfiguration || isMassSingleConfiguration
        ? `${routeUrls.dashboardChildren.cameraChildren.sensor_event_detection}/${cameraId}`
        : routeUrls.dashboardChildren.cameraChildren.sensor_event_detection,
      {
        state: {
          isSingleConfiguration: isSingleConfiguration,
          isMassSingleConfiguration: isMassSingleConfiguration,
          cameraIds: cameraIds,
          cameraName: cameraName,
          model: model,
        },
      }
    );
  };

  const {
    data: dataSingleCameraConfig,
    isFetching: isFetchingSingleCameraConfig,
  } = useGetCameraConfigQuery(cameraId || "", {
    skip: (!isSingleConfiguration && !isMassSingleConfiguration) || !cameraId,
    refetchOnMountOrArgChange: true,
  });

  const memoizedInitialValues = React.useMemo(() => {
    const initialValues = systemSettingFormInitialValues(
      !isSingleConfiguration && !isMassSingleConfiguration
    );
    if (!dataSingleCameraConfig?.data?.current || !cameraId)
      return {
        ...initialValues,
        // for bulk edit mode, default speed unit to account default
        speed_unit: speedUnit === "KMH" ? "kph" : "mph", // nautical miles from account setting defaults to mph
      };
    const {
      data: { current },
    } = dataSingleCameraConfig;
    // here we seed store with freshly fetched configuration
    setTimeout(() => {
      dispatch(
        setSingleVMCameraCurrentConfigs({
          cid: cameraId,
          profile: current,
        })
      );
    }, 100);
    return {
      ...initialValues,
      volume: current.device.system_soundVolume,
      speed_unit: current.device.system_speedUnit,
      parking_photo: current.device.system_parkingPhoto,
      audio_alert: current.device.system_alertLanguage,
      driver_id_login_audio_hint: current.device.system_rfid,
      live_view_audio_hint: current.device.system_live_view_on_audioHint || current.device.system_live_view_off_audioHint,
      record_sound_in_cab: current.device.vr_recordSound,
      video_snapshots: current.device.vr_videoSnapshots,
      driver_id_mode: current.device.system_driver_check_in_prompt_mode,
      driver_id_verification_provider: current.device.system_driver_check_in_verification_provider,
    };
  }, [isSingleConfiguration, isMassSingleConfiguration, dataSingleCameraConfig]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={memoizedInitialValues}
      onSubmit={(values: Record<string, any>, { setSubmitting }) => {
        const navigationType = navigationTypeRef.current;
        const modelKey = `vr_${baseModel}_embedded_videoFormat`;
        const devicePayload: Record<string, any> = {
          system_soundVolume: values?.volume,
          system_speedUnit: values?.speed_unit,
          system_parkingPhoto: values?.parking_photo,
          system_alertLanguage: values?.audio_alert,
          system_rfid: values.driver_id_login_audio_hint,
          system_driver_check_in_prompt_mode: values.driver_id_login_audio_hint ? values.driver_id_mode : undefined,
          system_driver_check_in_verification_provider: values.driver_id_mode === undefined
            ? undefined
            : values.driver_id_mode === "normal"
              ? "system"
              : values.driver_id_mode === "enhanced"
                ? "client"
                : undefined,
          system_live_view_on_audioHint: values.live_view_audio_hint,
          system_live_view_off_audioHint: values.live_view_audio_hint,
          vr_recordSound: values.record_sound_in_cab,
          vr_videoSnapshots: values.video_snapshots,
        };
        const updateAllCameras = async () => {
          try {
            for (const id of cameraIds) {
              await updateConfigurationSingleApiDeviceTrigger({
                cameraId: id,
                device: devicePayload,
              }).unwrap();
            }
            toast.success("All cameras updated successfully");
            if (navigationType === "next") {
              handleRightNavigation();
            } else if (navigationType === "previous") {
              handleLeftNavigation();
            }
          } catch (err: any) {
            console.error(err);
            toast.error("Failed to update one or more cameras");
          } finally {
            setSubmitting(false);
          }
        };
        if (isSingleConfiguration || isMassSingleConfiguration) {
          devicePayload[modelKey] = values[modelKey]; // adding particular model
          updateConfigurationSingleApiDeviceTrigger({
            cameraId: cameraId,
            device: devicePayload,
            // use complete cached object
            // cameraId: cameraId,
            // ...thisCameraConfigInStore,
            // device: {
            //   ...thisCameraConfigInStore.device,
            //   ...devicePayload,
            // },
          })
            .unwrap()
            .then((data) => {
              if (data.message && data.result) {
                // update store with updated camera config
                dispatch(
                  setSingleVMCameraCurrentConfigs({
                    cid: cameraId as string,
                    profile: {
                      ...thisCameraConfigInStore,
                      device: {
                        ...thisCameraConfigInStore.device,
                        ...devicePayload,
                      },
                    },
                  })
                );
                toast.success("Updated Successfully");
                if (navigationType === "next") {
                  handleRightNavigation();
                } else if (navigationType === "previous") {
                  handleLeftNavigation();
                }
              } else {
                toast.error("Updation Failed");
              }
            })
            .catch((errors) => {
              console.error(errors);
              toast.error(errors?.join(""));
            })
            .finally(() => {
              setSubmitting(false);
            });
        } else {
          updateAllCameras();
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
        dirty,
        resetForm,
      }) => {
        return (
          <div className="p-4 bg-gray-100 relative">
            {isFetchingSingleCameraConfig && (
              <div className="absolute w-full h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/70 z-10 flex items-center justify-center">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt=""
                  className="size-12"
                />
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="flex text-[10px]">
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(memoizedInitialValues, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all">
                  {JSON.stringify(values, null, 2)}
                </pre>
                <pre className="flex w-1/3 whitespace-break-spaces break-all max-h-72 overflow-auto">
                  {JSON.stringify(thisCameraConfigInStore, null, 2)}
                </pre>
              </div>
            )}
            <HeaderView
              heading={t("heading")}
              handleSave={() => {
                navigationTypeRef.current = "none";
                handleSubmit();
              }}
              handleSaveAndNext={() => {
                navigationTypeRef.current = "next";
                handleSubmit();
              }}
              handlePrevious={() => {
                navigationTypeRef.current = "previous";
                handleSubmit();
              }}
              handleLeftNavigation={handleLeftNavigation}
              handleRestoreToDefault={() => resetForm()}
              disabled={
                isSubmitting ||
                (!isSingleConfiguration && !isMassSingleConfiguration && cameraIds?.length < 1)
              }
              index={0}
            />
            <div className="bg-white grid grid-cols-12">
              <SubHeaderView
                isSingleConfiguration={isSingleConfiguration}
                isMassSingleConfiguration={isMassSingleConfiguration}
                noOfCamera={cameraIds?.length || 0}
                cameraName={cameraName}
              />
              <div className="col-span-12 grid grid-cols-12">
                <div className="col-span-3 border-r">
                  {SideMenu.map((item, index) => (
                    <div className="w-full font-semibold text-sm leading-none tracking-normal px-5 py-4 cursor-pointer hover:bg-accent-blue-paleO66 cursor-pointer"
                     onClick={(e) => {
                      setSelectedSettings(item.name);
                    }}>
                    <label
                      key={index}
                      className={`flex items-center p-3 bg-white rounded-lg shadow-md shadow-black/5 border ${selectedSettings === item.name
                          ? "border-accent-blue-dark bg-blue-50"
                          : "border-gray-200 bg-white"
                        }`}
                    >
                      <input
                        type="radio"
                        name="settings"
                        value={item.name}
                        checked={selectedSettings === item.name}
                        onChange={() => setSelectedSettings(item.name)}
                        className="h-4 w-4 shrink-0 rounded-full appearance-auto accent-blue-600"
                      />

                      <span className={`pl-2 min-w-0 break-word text-sm font-medium`}>
                        {item.label}
                      </span>
                    </label>
                    </div>
                  ))}
                </div>

                <form
                  className="col-span-9 grid grid-cols-6 gap-6"
                  onSubmit={handleSubmit}
                >
                  {selectedSettings === "system" ? (
                    <CameraSystemSettings
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  ) : (
                    <VideoRecordingSettings
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      model={baseModel}
                      isSingleConfiguration={isSingleConfiguration || isMassSingleConfiguration}
                    />
                  )}
                </form>
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
export default ScreenCameraSettings;
